import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import Draggable from 'react-draggable';

import { ConfirmDialogService, ToastService, useDataTable, useDialogCrup } from '@iamsoftware/react-hooks';
import { AgreementTemplateService, AgreementTemplateService as Service } from './AgreementTemplateService';
import { FormFields } from './FormFields';
import { TemplateDetail } from './TemplateDetail';
import { CommonService } from '../../../service/CommonService';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';

import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { AgreementService } from 'src/service/AgreementService';
import { GoogleService } from '../../quan-ly-tai-lieu/GoogleService';
import { AgreementConfigService } from 'src/service/AgreementConfigService';

import { Step1BasicInfo } from './components/Step1BasicInfo';
import { Step2Roles } from './components/Step2Roles';
import { Step3Positions } from './components/Step3Positions';
import { Step4Fields } from './components/Step4Fields';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const normalizeLocationSeq = (items: any[]) => {
  return items.map((item, index) => ({
    ...item,
    locationSeqId: (index + 1).toString(),
    sequenceNum: (index + 1).toString()
  }));
};

const statuses = [
  { value: 'N', label: 'Đang hoạt động' },
  { value: 'Y', label: 'Ngừng hoạt động' }
];

// --- EditorDialog component ---
const EditorDialog = ({ display, setDisplay, onSaved }: {
  display: any;
  setDisplay: (v: any) => void;
  onSaved?: (fileId: string, fileName: string, referenceFileId?: string, contentLocation?: string) => void;
}) => {
  const [fileId, setFileId] = useState<string | null>(null);
  const [originalFileId, setOriginalFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (display?.fileId) {
      setFileId(display.fileId);
      setOriginalFileId(display.originalFileId || null);
      setFileName(display.fileName || '');
    }
  }, [display]);

  const handleSave = async () => {
    if (!fileId) return;
    if (!fileName.trim()) {
      ToastService.error('Vui lòng nhập tên tài liệu');
      return;
    }
    setSaving(true);
    try {
      let res;
      if (originalFileId) {
        // Nếu đã có file gốc trên MinIO, dùng saveEditor
        res = await AgreementService.saveEditor(null, originalFileId, fileId);
      } else {
        // Nếu file mới tạo từ Google Drive, dùng syncEditorToMinio
        res = await AgreementService.syncEditorToMinio(fileId, fileName.trim(), 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      }

      if (res?.success === 'Y' || res?.fileId) {
        await Oauth2GoogleService.closeEditor(fileId);
        ToastService.success('Tài liệu đã được lưu thành công!');
        const finalFileId = res.fileId || originalFileId || fileId;
        onSaved?.(finalFileId, fileName.trim(), res.referenceFileId, res.contentLocation);
        setDisplay(null);
      } else {
        ToastService.error(res?.error || 'Không thể lưu tài liệu');
      }
    } catch {
      ToastService.error('Lỗi khi lưu tài liệu');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (fileId) {
      try {
        await Oauth2GoogleService.closeEditor(fileId);
      } catch (err) {
        console.error('Error closing editor:', err);
      }
    }
    setDisplay(null);
  };

  return (
    <Dialog
      header="Soạn thảo tài liệu"
      visible={!!display}
      style={{ width: '98vw' }}
      position="top"
      onHide={handleClose}
      footer={
        <div className="flex justify-content-end gap-2">
          <span className="p-inputgroup" style={{ maxWidth: 360 }}>
            <span className="p-inputgroup-addon"><i className="pi pi-file" /></span>
            <InputText value={fileName} onChange={e => setFileName(e.target.value)} placeholder="Nhập tên tài liệu" />
          </span>
          <Button label="Lưu tài liệu" icon="pi pi-save" type="button" severity="success" size="small"
            loading={saving} onClick={handleSave} />
          <Button label="Đóng" icon="pi pi-times" type="button" outlined severity="secondary" size="small"
            onClick={handleClose} />
        </div>
      }
    >
      {fileId && (
        <iframe
          src={`https://docs.google.com/document/d/${fileId}/edit`}
          width="100%"
          height="800"
          style={{ border: 'none' }}
          referrerPolicy="no-referrer"
        />
      )}
    </Dialog >
  );
};

// --- PDF Preview Dialog using canvas (pdfjs-dist) ---
const PdfPreviewDialog = ({ visible, onHide, fileId, title }: {
  visible: boolean;
  onHide: () => void;
  fileId: string | null;
  title?: string;
}) => {
  const [canvases, setCanvases] = useState<HTMLCanvasElement[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!visible || !fileId) {
      setCanvases([]);
      setCurrentPage(1);
      return;
    }
    loadPdf(fileId);
  }, [visible, fileId]);

  const renderPage = async (pdf: any, pageNumber: number) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas;
  };

  const loadPdf = async (fId: string) => {
    setLoading(true);
    try {
      const blob = await AgreementService.downloadPdf(fId);
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const promises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        promises.push(renderPage(pdf, i));
      }
      const result = await Promise.all(promises);
      setCanvases(result);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error rendering PDF:', err);
      ToastService.error('Lỗi khi hiển thị file PDF');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = canvases.length;

  return (
    <Dialog
      header={title || 'Xem tài liệu mẫu'}
      visible={visible}
      position="top"
      draggable={false}
      resizable={false}
      modal
      style={{ width: '70vw', maxHeight: '95vh' }}
      onHide={onHide}
      footer={
        <div className="flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-2">
            {totalPages > 0 && (
              <>
                <Button icon="pi pi-chevron-left" text size="small"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
                <span className="text-sm">Trang {currentPage} / {totalPages}</span>
                <Button icon="pi pi-chevron-right" text size="small"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
              </>
            )}
          </div>
          <Button label="Đóng" icon="pi pi-times" outlined severity="secondary" size="small"
            onClick={onHide} />
        </div>
      }
    >
      <div className="flex gap-2" style={{ height: 'calc(85vh - 120px)' }}>
        {/* Main content */}
        <div className="flex-1" style={{ backgroundColor: 'var(--surface-ground)', borderRadius: '8px', overflow: 'hidden' }}>
          {loading && (
            <div className="flex align-items-center justify-content-center h-full">
              <div className="text-center">
                <i className="pi pi-spin pi-spinner text-4xl mb-3" style={{ color: 'var(--primary-color)' }} />
                <p>Đang tải PDF...</p>
              </div>
            </div>
          )}
          {!loading && canvases.length > 0 && (
            <ScrollPanel style={{ width: '100%', height: '100%' }}>
              <div className="flex flex-column align-items-center p-3 gap-3">
                {canvases[currentPage - 1] && (
                  <div className="shadow-3" style={{ borderRadius: '4px', overflow: 'hidden', backgroundColor: '#fff' }}>
                    <img
                      src={canvases[currentPage - 1].toDataURL()}
                      alt={`Trang ${currentPage}`}
                      style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                )}
              </div>
            </ScrollPanel>
          )}
          {!loading && canvases.length === 0 && (
            <div className="flex align-items-center justify-content-center h-full">
              <div className="text-center">
                <i className="pi pi-file-pdf text-6xl mb-3" style={{ color: 'var(--text-color-secondary)' }} />
                <p style={{ color: 'var(--text-color-secondary)' }}>Không có file để hiển thị</p>
              </div>
            </div>
          )}
        </div>

        {/* Thumbnail sidebar */}
        {canvases.length > 1 && (
          <div style={{ width: '120px', flexShrink: 0 }}>
            <ScrollPanel style={{ width: '100%', height: '100%' }}>
              <div className="flex flex-column gap-2 p-1">
                {canvases.map((canvas, i) => (
                  <div
                    key={i}
                    className="cursor-pointer transition-all"
                    style={{
                      border: currentPage === i + 1 ? '2px solid var(--primary-color)' : '2px solid transparent',
                      borderRadius: '4px',
                      opacity: currentPage === i + 1 ? 1 : 0.7,
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    <img
                      src={canvas.toDataURL()}
                      alt={`Thumbnail ${i + 1}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', bottom: 0, right: 0,
                      backgroundColor: 'var(--primary-color)', color: '#fff',
                      padding: '1px 4px', fontSize: '10px', borderTopLeftRadius: '4px'
                    }}>
                      {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollPanel>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// --- TemplateWizardDialog component ---
const TemplateWizardDialog = ({
  visible,
  onHide,
  onFinish,
  documentTypeOptions,
  initialData = null,
  toast
}: {
  visible: boolean;
  onHide: () => void;
  onFinish: () => void;
  documentTypeOptions: any[];
  initialData?: any;
  toast: any;
}) => {
  const [step, setStep] = useState(1);
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 1: Info State
  const [formData, setFormData] = useState({
    agreementPseudoId: '',
    agreementName: '',
    agreementPartyTypeConfId: null,
    description: ''
  });
  const [errors, setErrors] = useState<any>({});
  const [file, setFile] = useState<any>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [editorDisplay, setEditorDisplay] = useState<any>(null);
  const [editorCreating, setEditorCreating] = useState(false);

  // Step 2: Roles State
  const [roles, setRoles] = useState<any[]>([]);
  const [availableRoleOptions, setAvailableRoleOptions] = useState<any[]>([]);
  const [showQuickRole, setShowQuickRole] = useState(false);
  const [quickRoleName, setQuickRoleName] = useState('');
  const [quickRoleIndex, setQuickRoleIndex] = useState<number | null>(null);

  // Step 3: Signature Positions State
  const [signaturePositions, setSignaturePositions] = useState<any[]>([]);
  const [canvases, setCanvases] = useState<HTMLCanvasElement[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const canvasRefs = useRef<any[]>([]);
  const draggableRefs = useRef({} as any);
  const containerRef = useRef<HTMLDivElement>(null);
  const [roleSearching, setRoleSearching] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const pollIntervalRef = useRef<any>(null);

  useEffect(() => {
    // Polling logic khi ở bước 3 mà chưa có PDF preview
    if (visible && step === 3 && agreementId && !previewFileId) {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            console.log('>>> Polling PDF status for:', agreementId);
            const res = await Service.get(agreementId);
            if (res?.referenceFileId) {
              setPreviewFileId(res.referenceFileId);
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          } catch (e) {
            console.error('Polling error', e);
          }
        }, 3000);
      }
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [visible, step, agreementId, previewFileId]);

  // Step 4: Form Fields State
  const [dbFormFields, setDbFormFields] = useState<any[]>([]);
  const [formFieldTypes, setFormFieldTypes] = useState<any[]>([]);
  const [mapProperties, setMapProperties] = useState<any[]>([]);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (visible) {
      if (initialData?.agreementId) {
        setAgreementId(initialData.agreementId);
        loadAgreementData(initialData.agreementId);
        setStep(1);
      } else {
        setStep(1);
        setAgreementId(null);
        setFormData({
          agreementPseudoId: '',
          agreementName: '',
          agreementPartyTypeConfId: null,
          description: ''
        });
        setErrors({});
        setFile(null);
        setFileId(null);
        setPreviewFileId(null);
        setFileName('');
        setRoles([]);
        setSignaturePositions([]);
        setDbFormFields([]);
        setMapProperties([]);
      }
    }
  }, [visible, initialData]);

  useEffect(() => {
    if (visible && step === 2 && availableRoleOptions.length === 0) {
      loadRoles();
    }
    if (visible && step === 3 && previewFileId) {
      loadPdf(previewFileId);
    }
    if (visible && formFieldTypes.length === 0) {
      loadFormFieldTypes();
    }
  }, [visible, step, previewFileId]);

  // Sync signaturePositions with roles
  useEffect(() => {
    if (!signaturePositions.length) return;
    const validRoleTypeIds = roles.map(r => r.roleTypeId).filter(Boolean);
    setSignaturePositions(prev => {
      // Remove boxes whose role was deleted (using roleTypeId as primary key in Wizard)
      const filtered = prev.filter(pos => validRoleTypeIds.includes(pos.roleTypeId));
      // Update info for remaining boxes
      return filtered.map(pos => {
        const role = roles.find(r => r.roleTypeId === pos.roleTypeId);
        if (!role) return pos;
        if (pos.partyId !== role.partyId || pos.roleTypeName !== role.roleTypeName) {
          return { ...pos, partyId: role.partyId, roleTypeName: role.roleTypeName };
        }
        return pos;
      });
    });
  }, [roles]);

  const loadRoles = async () => {
    setRoleSearching(true);
    try {
      const res = await AgreementConfigService.findRoles('');
      if (res?.listData) {
        setAvailableRoleOptions(res.listData.map((r: any) => ({
          label: r.description,
          value: r.roleTypeId
        })));
      }
    } catch (e) {
      console.error('Load roles error', e);
    } finally {
      setRoleSearching(false);
    }
  };

  const loadAgreementData = async (id: string) => {
    setLoading(true);
    try {
      const [res] = await Promise.all([
        Service.get(id),
        availableRoleOptions.length === 0 ? loadRoles() : Promise.resolve()
      ]);
      console.log('Template Wizard Data from BE:', res);
      if (res) {
        setFormData({
          agreementPseudoId: res.agreementPseudoId || '',
          agreementName: res.agreementName || '',
          agreementPartyTypeConfId: res.agreementPartyTypeConfId || null,
          description: res.description || ''
        });
        setFileId(res.fileId || null);
        setPreviewFileId(res.referenceFileId || null);
        setFileName(res.fileName || '');

        if (res.roles) {
          setRoles(res.roles.map((r: any) => ({
            roleTypeId: r.roleTypeId,
            roleTypeName: r.roleTypeName || r.description || r.partyName || '',
            partyId: r.partyId
          })));
        }

        if (res.signaturePositions) {
          setSignaturePositions(res.signaturePositions.map((p: any) => ({
            ...p,
            pageIndex: p.pageNumber - 1,
            x: p.ulx,
            y: p.uly
          })));
        }

        if (res.dbFormFields) {
          setDbFormFields(res.dbFormFields.map((f: any) => ({
            ...f,
            isRequired: f.isRequired === 'Y'
          })));
        }

        if (res.mapProperties) {
          setMapProperties(res.mapProperties);
        }
      }
    } catch (e) {
      console.error('Load agreement data error', e);
      ToastService.error('Không thể tải dữ liệu tài liệu mẫu');
    } finally {
      setLoading(false);
    }
  };

  const loadPdf = async (fId: string) => {
    setPdfLoading(true);
    try {
      const blob = await AgreementService.downloadPdf(fId);
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const promises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        promises.push(renderPage(pdf, i));
      }
      const result = await Promise.all(promises);
      setCanvases(result);
    } catch (e) {
      console.error('Error loading PDF', e);
      ToastService.error('Không thể tải PDF xem trước');
    } finally {
      setPdfLoading(false);
    }
  };

  const renderPage = async (pdf: any, pageNumber: number) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas;
  };

  const handleExtractFields = async () => {
    setExtracting(true);
    try {
      const res = await Service.extractFields(fileId || undefined, (!fileId && file) ? file : undefined);
      if (res?.dbFormFields) {
        const extracted = res.dbFormFields.map((field: any) => {
          const existing = dbFormFields.find(f => f.fieldName === field.fieldName);
          return existing ? { ...field, ...existing } : { ...field, isRequired: false };
        });
        setDbFormFields(extracted);
        if (res.mapProperties) {
          setMapProperties(res.mapProperties);
        }
        setStep(4);
      } else {
        ToastService.error(res?.error || 'Không thể trích xuất trường thông tin');
      }
    } catch (e) {
      console.error('Extract fields error', e);
      ToastService.error('Lỗi khi trích xuất trường thông tin');
    } finally {
      setExtracting(false);
    }
  };

  const loadFormFieldTypes = async () => {
    try {
      const res = await Service.getFormFieldTypes();
      if (res?.listData) {
        setFormFieldTypes(res.listData.map((item: any) => ({
          label: item.description,
          value: item.enumId
        })));
      }
    } catch (e) {
      console.error('Load form field types error', e);
    }
  };

  const validateStep = (s: number) => {
    if (s === 1) {
      const newErrors: any = {};
      if (!formData.agreementPseudoId) newErrors.agreementPseudoId = 'Vui lòng nhập mã tài liệu mẫu';
      if (!formData.agreementName) newErrors.agreementName = 'Vui lòng nhập tên tài liệu mẫu';
      if (!formData.agreementPartyTypeConfId) newErrors.agreementPartyTypeConfId = 'Vui lòng chọn loại tài liệu';
      if (!fileId && !file) newErrors.file = 'Vui lòng tải lên tệp mẫu';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return { valid: false, message: 'Vui lòng điền đầy đủ thông tin bắt buộc và tải lên tệp mẫu ở Bước 1' };
      }
    } else if (s === 2) {
      if (roles.length === 0) return { valid: false, message: 'Vui lòng thêm ít nhất một đối tượng ký ở Bước 2' };
      if (roles.some(r => !r.roleTypeId)) return { valid: false, message: 'Vui lòng chọn vai trò cho tất cả các đối tượng ký ở Bước 2' };
    } else if (s === 3) {
      const rolesWithoutPositions = roles.filter(role =>
        !signaturePositions.some(pos => pos.roleTypeId === role.roleTypeId)
      );
      if (rolesWithoutPositions.length > 0) {
        return { valid: false, message: 'Vui lòng thiết lập vị trí ký cho tất cả các vai trò ở Bước 3' };
      }
    }
    return { valid: true };
  };

  const handleStepClick = async (targetStep: number) => {
    if (targetStep === step) return;
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }

    // Validate all steps from current to targetStep - 1
    for (let i = 1; i < targetStep; i++) {
      const check = validateStep(i);
      if (!check.valid) {
        ToastService.error(check.message);
        setStep(i);
        return;
      }
    }

    // Special handling when jumping to step 3 or 4
    // Special handling when jumping to step >= 3
    // Loginc lưu nháp khi chuyển từ Bước 1 sang các bước sau (qua click Stepper)
    if (step === 1 && targetStep > 1) {
      if (file && !fileId) {
        setLoading(true);
        try {
          const res = await AgreementService.uploadFile(file);
          if (res?.fileId || res?.id) {
            setFileId(res.fileId || res.id);
            setPreviewFileId(res.referenceFileId);
          }
        } catch (e) {
          console.error('Step jump upload error', e);
        } finally {
          setLoading(false);
        }
      }
    }

    if (targetStep === 4 && step < 4) {
      handleExtractFields();
    } else {
      if (targetStep === 3 && fileId && !previewFileId) {
        AgreementService.generatePdf(fileId, fileName || '', undefined, undefined).then(res => {
          if (res?.referenceFileId) setPreviewFileId(res.referenceFileId);
        });
      }
      setStep(targetStep);
    }
  };

  const handleNext = async () => {
    const check = validateStep(step);
    if (!check.valid) {
      ToastService.error(check.message);
      return;
    }

    if (step === 1) {
      if (file && !fileId) {
        setLoading(true);
        try {
          const res = await AgreementService.uploadFile(file);
          if (res?.fileId || res?.id) {
            setFileId(res.fileId || res.id);
            setPreviewFileId(res.referenceFileId);
          }
        } catch (e) {
          ToastService.error('Lỗi khi tải tệp lên');
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }
      setStep(2);
    } else if (step === 2) {
      if (fileId && !previewFileId) {
        AgreementService.generatePdf(fileId, fileName || '', undefined, undefined).then(res => {
          if (res?.referenceFileId) setPreviewFileId(res.referenceFileId);
        });
      }
      setStep(3);
    } else if (step === 3) {
      handleExtractFields();
    } else if (step === 4) {
      setSaving(true);
      try {
        const finalizedRoles = roles.map(r => ({
          roleTypeId: r.roleTypeId,
          description: r.roleTypeName || ''
        }));

        const normalizedPositions = normalizeLocationSeq(signaturePositions);

        const finalizedPositions = normalizedPositions.map(pos => {
          const pageIdx = Number(pos.pageIndex);
          const canvas = canvasRefs.current[pageIdx];
          const pageWidth = canvas?.width || 0;
          const pageHeight = canvas?.height || 0;
          const widthPx = Number(pos.widthPx);
          const heightPx = Number(pos.heightPx);
          const fontSize = Number(pos.fontSize);
          const x = Number(pos.x);
          const y = Number(pos.y);

          const partyId = pos.partyId || selectedRole?.partyId || selectedRole?.value;
          const role = roles.find((r: any) => r.partyId === partyId);

          return {
            locationSeqId: pos.locationSeqId,
            partyId: partyId,
            roleTypeId: pos.roleTypeId || role?.roleTypeId || selectedRole?.roleTypeId || null,
            signatureTypeEnumId: pos.signatureTypeEnumId || 'AgtSignTraditional',
            pageNumber: pageIdx + 1,
            pageWidth: pageWidth,
            pageHeight: pageHeight,
            widthPx: widthPx,
            heightPx: heightPx,
            ulx: x,
            uly: y,
            fontSize: fontSize
          };
        });

        const finalizedFields = dbFormFields.map(f => ({
          ...f,
          isRequired: f.isRequired ? 'Y' : 'N'
        }));

        const payload = {
          agreementPseudoId: formData.agreementPseudoId,
          agreementName: formData.agreementName,
          agreementPartyTypeConfId: formData.agreementPartyTypeConfId,
          description: formData.description,
          fileId: fileId,
          referenceFileId: previewFileId,
          fileName: fileName,
          roles: finalizedRoles,
          signaturePositions: finalizedPositions,
          dbFormFields: finalizedFields
        }

        let currentAgtId = agreementId;
        if (!currentAgtId) {
          try{
            const res = await Service.create(payload);
            currentAgtId = res?.agreementId || res?.id;
          }catch(err){
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.response?.data?.errors || 'Lỗi khi lưu tài liệu mẫu' });
            return;
          }
        } else {
          try{
            await Service.update(currentAgtId, payload);
          }catch(err){
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.response?.data?.errors || 'Lỗi khi lưu tài liệu mẫu' });
            return;
          }
        }

        if (!currentAgtId) {
          throw new Error('Không thể lưu tài liệu mẫu');
        }

        toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã lưu tài liệu mẫu thành công!' });
        onFinish();
        onHide();
      } catch (err: any) {
        console.error('Save error', err);
        toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Lỗi khi lưu tài liệu mẫu' });
      } finally {
        setSaving(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleCreateNewDoc = async () => {
    setEditorCreating(true);
    try {
      const defaultName = formData.agreementName || 'Tai_lieu_mau_moi';
      const res = await Oauth2GoogleService.create({ fileName: defaultName, contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      if (res?.fileId || res?.id) {
        const newFileId = res.fileId || res.id;
        await GoogleService.openAnyoneView(null as any, newFileId);
        setEditorDisplay({ fileId: newFileId, fileName: defaultName });
      }
    } catch (err) {
      ToastService.error('Lỗi khi tạo tài liệu mới');
    } finally {
      setEditorCreating(false);
    }
  };
  const handleEditEditor = async () => {
    if (!fileId) return;
    try {
      if (fileId.includes('/')) {
        // File ở MinIO
        const res = await AgreementService.openEditor(null, fileId);
        if (res?.googleFileId) {
          setEditorDisplay({ fileId: res.googleFileId, fileName, originalFileId: fileId });
        }
      } else {
        // File ở Google Drive (có thể là file tạm hoặc file cũ chưa sync)
        await GoogleService.openAnyoneView(null as any, fileId);
        setEditorDisplay({ fileId, fileName });
      }
    } catch (err) {
      ToastService.error('Lỗi khi mở trình soạn thảo');
    }
  };

  const addRole = () => {
    setRoles([...roles, { roleTypeId: '', partyId: `ROLE_${Date.now()}` }]);
  };

  const removeRole = (index: number) => {
    const newRoles = [...roles];
    newRoles.splice(index, 1);
    setRoles(newRoles);
  };

  const updateRole = (index: number, roleTypeId: string) => {
    const newRoles = [...roles];
    const roleType = availableRoleOptions.find(opt => opt.value === roleTypeId);
    newRoles[index] = { ...newRoles[index], roleTypeId, roleTypeName: roleType?.label };
    setRoles(newRoles);
  };

  const handleCreateQuickRole = async () => {
    if (!quickRoleName.trim()) {
      ToastService.error('Vui lòng nhập tên vai trò');
      return;
    }
    setSaving(true);
    try {
      const res = await AgreementConfigService.createRole({ description: quickRoleName });
      if (res?.roleTypeId || res?.id) {
        const newRoleId = res.roleTypeId || res.id;
        ToastService.success('Đã tạo vai trò mới thành công');

        // Refresh options
        await loadRoles();

        // Auto map to row
        if (quickRoleIndex !== null) {
          const newRoles = [...roles];
          newRoles[quickRoleIndex] = {
            ...newRoles[quickRoleIndex],
            roleTypeId: newRoleId,
            roleTypeName: quickRoleName
          };
          setRoles(newRoles);
        }

        setShowQuickRole(false);
        setQuickRoleName('');
      } else {
        ToastService.error(res?.error || 'Lỗi khi tạo vai trò');
      }
    } catch (err: any) {
      ToastService.error(err.message || 'Lỗi khi tạo vai trò');
    } finally {
      setSaving(false);
    }
  };

  const addSignatureBox = (role: any, pageIndex?: number, x?: number, y?: number) => {
    const defaultWidth = 200;
    const defaultHeight = 65;
    const newBox = {
      locationSeqId: (signaturePositions.length + 1).toString(),
      partyId: role.partyId,
      roleTypeId: role.roleTypeId,
      roleTypeName: role.roleTypeName,
      pageIndex: pageIndex !== undefined ? pageIndex : currentPage - 1,
      x: x !== undefined ? x : 50,
      y: y !== undefined ? y : 50,
      widthPx: defaultWidth,
      heightPx: defaultHeight,
      fontSize: 12
    };
    setSignaturePositions([...signaturePositions, newBox]);
  };

  const handleDrop = (event: any, pageIndex: number) => {
    event.preventDefault();
    if (!selectedRole) return;

    const rect = canvasRefs.current[pageIndex]?.getBoundingClientRect();
    if (!rect) return;

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const cursorX = event.clientX - rect.left;
    const cursorY = event.clientY - rect.top;

    const defaultWidth = 200;
    const defaultHeight = 65;
    const xMax = rect.width - defaultWidth;
    const yMax = rect.height - defaultHeight;
    const x = clamp(cursorX, 0, Math.max(0, xMax));
    const y = clamp(cursorY, 0, Math.max(0, yMax));

    addSignatureBox(selectedRole, pageIndex, x, y);
  };

  const handleDragStop = (event: any, data: any, locationSeqId: string) => {
    const updated = signaturePositions.map(pos => {
      if (pos.locationSeqId === locationSeqId) {
        const canvas = canvasRefs.current[pos.pageIndex];
        if (!canvas) return pos;
        const maxX = canvas.width - pos.widthPx;
        const maxY = canvas.height - pos.heightPx;
        return {
          ...pos,
          x: Math.min(Math.max(data.x, 0), Math.max(0, maxX)),
          y: Math.min(Math.max(data.y, 0), Math.max(0, maxY))
        };
      }
      return pos;
    });
    setSignaturePositions(normalizeLocationSeq(updated));
  };

  const renderStepIndicators = () => (
    <div className="flex align-items-center justify-content-center gap-2 mb-5 py-3 border-round-xl bg-gray-50 shadow-1 surface-ground">
      {[1, 2, 3, 4].map(s => (
        <React.Fragment key={s}>
          <div className="flex flex-column align-items-center gap-2 px-3 relative cursor-pointer" onClick={() => handleStepClick(s)}>
            <div className={`border-circle w-3rem h-3rem flex align-items-center justify-content-center font-bold shadow-2 transition-all transition-duration-300
              ${step === s ? 'bg-primary text-white scale-110' : step > s ? 'bg-green-500 text-white' : 'bg-white text-400 border-1'}`}>
              {step > s ? <i className="pi pi-check" /> : s}
            </div>
            <span className={`text-xs white-space-nowrap font-semibold uppercase tracking-wider
              ${step === s ? 'text-primary' : step > s ? 'text-green-600' : 'text-400'}`}>
              {s === 1 ? 'Thông tin' : s === 2 ? 'Đối tượng' : s === 3 ? 'Vị trí' : 'Trường biến'}
            </span>
          </div>
          {s < 4 && (
            <div className="flex-1 px-1 flex align-items-center" style={{ minWidth: 60, height: 48 }}>
              <div className={`h-2px w-full transition-all transition-duration-500 ${step > s ? 'bg-green-500' : 'bg-gray-300'}`} />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <Dialog
      header={initialData ? 'Sửa tài liệu mẫu' : 'Thêm mới tài liệu mẫu'}
      visible={visible}
      style={{ width: '98vw' }}
      maximized={false}
      modal
      onHide={onHide}
      contentClassName="surface-ground p-3"
      headerClassName="bg-white border-bottom-1 border-300"
      footer={
        <div className="flex justify-content-end gap-2 p-3 bg-white border-top-1 border-300">
          {step > 1 && <Button label="Quay lại" icon="pi pi-arrow-left" outlined severity="secondary" onClick={() => setStep(step - 1)} />}
          <Button label={step === 4 ? 'Hoàn tất' : 'Tiếp tục'} icon={step === 4 ? 'pi pi-check' : 'pi pi-arrow-right'}
            loading={loading || saving || extracting} onClick={handleNext} />
        </div>
      }
    >
      <Dialog header="Thêm nhanh vai trò" visible={showQuickRole} style={{ width: '30vw' }} modal onHide={() => setShowQuickRole(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Hủy" icon="pi pi-times" text severity="secondary" onClick={() => setShowQuickRole(false)} />
            <Button label="Lưu" icon="pi pi-check" loading={saving} onClick={handleCreateQuickRole} />
          </div>
        }>
        <div className="flex flex-column gap-2">
          <label htmlFor="newRoleName">Tên vai trò <span className="text-red-500">*</span></label>
          <InputText id="newRoleName" value={quickRoleName} onChange={e => setQuickRoleName(e.target.value)} placeholder="Nhập tên vai trò..." autoFocus />
        </div>
      </Dialog>

      <div style={{ minHeight: '400px', height: '80vh' }} className="flex flex-column">
        {renderStepIndicators()}
        <div className="flex-1 overflow-hidden">
          {step === 1 && (
            <Step1BasicInfo
              formData={formData}
              setFormData={(data) => {
                setFormData(data);
                // Clear errors on change
                if (errors.agreementPseudoId && data.agreementPseudoId) setErrors((prev: any) => ({ ...prev, agreementPseudoId: null }));
                if (errors.agreementName && data.agreementName) setErrors((prev: any) => ({ ...prev, agreementName: null }));
                if (errors.agreementPartyTypeConfId && data.agreementPartyTypeConfId) setErrors((prev: any) => ({ ...prev, agreementPartyTypeConfId: null }));
              }}
              file={file}
              fileName={fileName}
              onFileChange={async (f) => {
                setFile(f);
                setFileName(f.name);
                setFileId(null);
                setPreviewFileId(null);
                setErrors((prev: any) => ({ ...prev, file: null }));

                // Tối ưu: Upload luôn để lấy PDF
                setLoading(true);
                try {
                  const res = await AgreementService.uploadFile(f);
                  if (res?.fileId || res?.id) {
                    setFileId(res.fileId || res.id);
                    setPreviewFileId(res.referenceFileId);
                  }
                } catch (err) {
                  console.error('Upload fast error', err);
                } finally {
                  setLoading(false);
                }
              }}
              documentTypeOptions={documentTypeOptions}
              onShowEditor={handleCreateNewDoc}
              onEditEditor={handleEditEditor}
              editorCreating={editorCreating}
              fileId={fileId}
              readonly={loading || saving}
              errors={errors}
            />
          )}

          {step === 2 && (
            <Step2Roles
              roles={roles}
              setRoles={setRoles}
              availableRoleOptions={availableRoleOptions}
              roleSearching={roleSearching}
              onAddRole={addRole}
              onRemoveRole={removeRole}
              onQuickRole={(index) => {
                setQuickRoleIndex(index);
                setShowQuickRole(true);
              }}
            />
          )}

          {step === 3 && (
            <div className="relative" style={{ height: '70vh' }}>
              {!previewFileId ? (
                <div className="flex flex-column align-items-center justify-content-center h-full bg-white border-round shadow-1">
                  <i className="pi pi-spin pi-spinner text-5xl mb-3" style={{ color: 'var(--primary-color)' }} />
                  <h3 className="m-0 text-900">Đang chuẩn bị bản xem trước PDF...</h3>
                  <p className="text-600 mt-2">Hệ thống đang chuyển đổi tệp Docx sang PDF để thiết lập vị trí ký.</p>
                  <p className="text-500 text-sm mt-1">(Quá trình này thường mất từ 5-10 giây)</p>
                </div>
              ) : (
                <Step3Positions
                  roles={roles}
                  selectedRole={selectedRole}
                  setSelectedRole={setSelectedRole}
                  signaturePositions={signaturePositions}
                  setSignaturePositions={setSignaturePositions}
                  pdfLoading={pdfLoading}
                  canvases={canvases}
                  canvasRefs={canvasRefs}
                  draggableRefs={draggableRefs}
                  handleDrop={handleDrop}
                  handleDragStop={handleDragStop}
                />
              )}
            </div>
          )}
          {step === 4 && (
            <Step4Fields
              dbFormFields={dbFormFields}
              setDbFormFields={setDbFormFields}
              formFieldTypes={formFieldTypes}
              roles={roles.map(r => ({ label: r.roleTypeName || r.description || r.roleTypeId, value: r.roleTypeId }))}
              mapProperties={mapProperties}
            />
          )}
        </div>
      </div>

      <EditorDialog
        display={editorDisplay}
        setDisplay={setEditorDisplay}
        onSaved={(id, name, refId, contentLoc) => {
          setFileId(id);
          setFileName(name);
          setPreviewFileId(refId || null);
          setFile(null);
        }}
      />
    </Dialog >
  );
};

// --- Main component ---
export const TemplateDocumentConfig: React.FC = () => {
  const dataKey = 'agreementId';
  const [statusFilter, setStatusFilter] = useState(statuses[0].value);
  const [keyword, setKeyword] = useState('');
  const [documentTypeOptions, setDocumentTypeOptions] = useState<any[]>([]);
  const [documentTypeMap, setDocumentTypeMap] = useState<Record<string, string>>({});

  const toast = useRef<Toast>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Wizard dialog state
  const [wizardVisible, setWizardVisible] = useState(false);
  const [wizardData, setWizardData] = useState<any>(null);

  // Additional dialogs state
  const [formFieldsDisplay, setFormFieldsDisplay] = useState<any>(null);
  const [detailDialog, setDetailDialog] = useState<any>(null);

  // PDF preview dialog state
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [pdfPreviewFileId, setPdfPreviewFileId] = useState<string | null>(null);


  useEffect(() => {
    CommonService.getAgreementType().then(data => {
      if (data?.listData) {
        const options = data.listData.map((item: any) => ({
          value: item.agreementPartyTypeConfId,
          label: item.agreementConfigureName
        }));
        setDocumentTypeOptions(options);
        const map: Record<string, string> = {};
        data.listData.forEach((item: any) => {
          if (item.agreementConfigureEnumId) {
            map[item.agreementConfigureEnumId] = item.agreementConfigureName;
          }
        });
        setDocumentTypeMap(map);
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    reloadLazyData();
  }, [statusFilter]);

  const handleTabChange = (e: { index: number }) => {
    setActiveTabIndex(e.index);
    setStatusFilter(statuses[e.index].value);
  };

  const { render: renderDataTable, selectedItems, reloadLazyData } = useDataTable({
    tableHeader: (
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <Button
          className="ml-auto"
          label="Tạo tài liệu mẫu"
          icon="pi pi-plus"
          size="small"
          severity="success"
          onClick={handleCreate}
        />
      </div>
    ),
    dataKey,
    columns: [
      {
        field: 'agreementPseudoId',
        header: 'Mã tài liệu mẫu',
        width: 130,
        matchMode: 'contains',
        dataType: 'custom',
        customCell(rowData: any) {
          return (
            <span className="underline text-primary cursor-pointer"
              onDoubleClick={() => handleEdit(rowData, true)}
              title="Double click để xem chi tiết">
              {rowData.agreementPseudoId}
            </span>
          );
        }
      },
      { field: 'agreementName', header: 'Tên tài liệu mẫu', width: 220, matchMode: 'contains' },
      { field: 'fileName', header: 'Tên file mẫu', width: 200, matchMode: 'contains' },
      {
        field: 'agreementConfigureEnumId',
        header: 'Loại tài liệu',
        width: 220,
        dataType: 'custom',
        customCell(rowData: any) {
          const code = rowData.agreementConfigureEnumId;
          return documentTypeMap[code] || code || '';
        }
      },
      { field: 'agreementDate', header: 'Ngày tạo', width: 130, dataType: 'date' },
      { field: 'description', header: 'Mô tả', width: 220, matchMode: 'contains' },
      { field: 'status', header: 'Trạng thái', width: 140 }
    ],
    indexColumnWidth: 45,
    actionColumnWidth: 120,
    getList: lazyLoadEvent => {
      const params = keyword ? { textSearch: keyword.trim() } : undefined;
      return Service.getAgreementList(statusFilter, lazyLoadEvent, params);
    },
    initActions(items: any[]) {
      items?.forEach(item => {
        const actions: any[] = [
          { icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => handleEdit(item) },
          { icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => handleEdit(item, true) },
          { icon: 'pi pi-trash', title: 'Xóa', className: 'p-button-danger', command: () => handleDelete(item) }
        ];
        if (item.statusId === 'AGT_ACTIVE') {
          actions.push({
            icon: 'pi pi-lock', title: 'Khóa', className: 'p-button-danger',
            command: () => handleLock(item)
          });

        } else if (item.statusId === 'AGT_INACTIVE') {
          actions.push({
            icon: 'pi pi-unlock', title: 'Mở khóa', className: 'p-button-success',
            command: () => handleUnlock(item)
          });
        }
        item.actions = actions;
      });
    },
    originalProps: { scrollHeight: 'calc(100vh - 300px)' }
  });

  const selectedRow = selectedItems?.[dataKey] ? selectedItems : null;

  function handleCreate() {
    setWizardData(null);
    setWizardVisible(true);
  }


  function handleEdit(rowData?: any, viewOnly?: boolean) {
    const id = resolveDataKey(rowData);
    if (!id) return;

    if (viewOnly) {
      setDetailDialog({ agreementId: id });
      return;
    }

    Service.get(id).then(data => {
      setWizardData(data);
      setWizardVisible(true);
    });
  }

  function resolveDataKey(rowData?: any) {
    if (rowData && rowData[dataKey]) return rowData[dataKey];
    if (selectedRow && selectedRow[dataKey]) return selectedRow[dataKey];
    return null;
  }

  function handleLock(item: any) {
    ConfirmDialogService.confirm(
      'Khóa tài liệu mẫu',
      'Bạn xác nhận khóa tài liệu mẫu này?',
      () => {
        Service.disable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }



  function handleUnlock(item: any) {
    ConfirmDialogService.confirm(
      'Mở khóa tài liệu mẫu',
      'Bạn xác nhận mở khóa tài liệu mẫu này?',
      () => {
        Service.enable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }

  function handleDelete(item: any) {
    ConfirmDialogService.confirm(
      'Xóa tài liệu mẫu',
      'Bạn xác nhận xóa tài liệu mẫu này? Hành động này không thể hoàn tác.',
      () => {
        Service.delete(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        }).catch(() => {
          ToastService.error("Xóa thất bại");
        });
      }
    );
  }


  return (
    <div className="card border-none shadow-1 border-round-xl" style={{ backgroundColor: 'var(--surface-ground)' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .tai-lieu-mau-table .p-datatable-thead > tr:first-child > th {
          position: sticky !important; top: 0 !important; z-index: 10 !important;
          background: var(--surface-card) !important;
        }
        .tai-lieu-mau-table .p-datatable-thead > tr:nth-child(2) > th {
          position: sticky !important; top: 38px !important; z-index: 9 !important;
          background: var(--surface-card) !important;
        }
      `}} />
      <div className="col-12">
        <h3 className="m-0 mb-2" style={{ color: 'var(--text-color)', fontWeight: 'bold' }}>Quản lý tài liệu mẫu</h3>
      </div>
      <div className="col-12 pb-0">
        <Toast ref={toast} />
        <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
          <TabPanel header="Đang hoạt động" />
          <TabPanel header="Ngừng hoạt động" />
        </TabView>
      </div>
      <div className="col-12 py-0 tai-lieu-mau-table">
        {renderDataTable()}
      </div>

      <TemplateWizardDialog
        visible={wizardVisible}
        onHide={() => setWizardVisible(false)}
        documentTypeOptions={documentTypeOptions}
        initialData={wizardData}
        toast={toast}
        onFinish={() => {
          setWizardVisible(false);
          reloadLazyData();
        }}
      />
      <FormFields display={formFieldsDisplay} setDisplay={setFormFieldsDisplay} />
      <TemplateDetail display={detailDialog} setDisplay={setDetailDialog} documentTypeOptions={documentTypeOptions} />


    </div>
  );
};
